import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";

function getPasswordStrength(pw: string) {
  if (!pw) return { score: 0, label: "", color: "transparent" };
  let score = 0;
  if (pw.length >= 8)           score++;
  if (pw.length >= 12)          score++;
  if (/[A-Z]/.test(pw))         score++;
  if (/[0-9]/.test(pw))         score++;
  if (/[^A-Za-z0-9]/.test(pw))  score++;
  if (score <= 1) return { score, label: "Weak",   color: "#ef4444" };
  if (score <= 3) return { score, label: "Fair",   color: "#f59e0b" };
  if (score === 4) return { score, label: "Good",  color: "#06b6d4" };
  return { score, label: "Strong", color: "#34d399" };
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [isDark,     setIsDark]     = useState(false);
  const [password,   setPassword]   = useState("");
  const [confirmPw,  setConfirmPw]  = useState("");
  const [showPw,     setShowPw]     = useState(false);
  const [showCfm,    setShowCfm]    = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [done,       setDone]       = useState(false);
  const [validLink,  setValidLink]  = useState<boolean | null>(null);
  const [isInvite,   setIsInvite]   = useState(false);

  const strength = getPasswordStrength(password);

  useEffect(() => {
    // Capture hash before Supabase clears it after token exchange
    const hash = window.location.hash;
    const hasToken = hash.includes("access_token");
    if (hash.includes("type=invite")) setIsInvite(true);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" && session) {
        setValidLink(true);
      } else if (event === "SIGNED_IN" && session && hasToken) {
        // Invite links fire SIGNED_IN instead of PASSWORD_RECOVERY
        setValidLink(true);
      } else if (event === "INITIAL_SESSION" && !hasToken) {
        // No recovery token in URL — resolve immediately based on session
        setValidLink(!!session);
      }
      // If hasToken: wait for PASSWORD_RECOVERY (don't short-circuit on INITIAL_SESSION)
    });

    // Hard fallback: if no auth event resolves within 5s, mark invalid
    const timeout = setTimeout(() => {
      setValidLink(prev => prev === null ? false : prev);
    }, 5000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8)    { setError("Password must be at least 8 characters."); return; }
    if (password !== confirmPw) { setError("Passwords do not match."); return; }
    setLoading(true); setError("");
    const { error: err } = await supabase.auth.updateUser({ password });
    if (err) { setError(err.message || "Could not update password. Please request a new reset link."); setLoading(false); return; }
    setDone(true);
    setTimeout(() => navigate(isInvite ? "/dashboard" : "/login"), 2500);
  };

  const D = isDark;
  const pageBg    = D ? "linear-gradient(150deg,#07101f 0%,#0a1628 55%,#07101f 100%)" : "linear-gradient(150deg,#f0f4ff 0%,#e8f0fe 40%,#fef9f0 100%)";
  const formBg    = D ? "#111c2e" : "#ffffff";
  const textMain  = D ? "#f1f0ed" : "#0f172a";
  const textSub   = D ? "rgba(241,240,237,0.55)" : "#334155";
  const textMuted = D ? "rgba(241,240,237,0.32)" : "#64748b";
  const labelClr  = D ? "rgba(241,240,237,0.65)" : "#1e3a5f";
  const inputBg   = D ? "rgba(255,255,255,0.06)" : "#f8fafc";
  const inputBdr  = D ? "rgba(255,255,255,0.11)" : "#cbd5e1";
  const phClr     = D ? "rgba(255,255,255,0.25)" : "#94a3b8";
  const footerBdr = D ? "rgba(255,255,255,0.07)" : "#e2e8f0";
  const errBg     = D ? "rgba(220,38,38,0.09)" : "#fef2f2";
  const errBdr    = D ? "rgba(220,38,38,0.28)" : "rgba(220,38,38,0.22)";
  const errTitle  = D ? "#f87171" : "#dc2626";
  const errHint   = D ? "rgba(241,240,237,0.48)" : "#9a3412";
  const captionClr= D ? "rgba(241,240,237,0.22)" : "#94a3b8";
  const tglBg     = D ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.06)";
  const tglBdr    = D ? "rgba(255,255,255,0.13)" : "rgba(15,23,42,0.1)";
  const tglClr    = D ? "#f1f0ed" : "#0f172a";
  const cardShadow= D ? "0 24px 64px rgba(0,0,0,0.6),0 4px 18px rgba(0,0,0,0.35)" : "0 24px 64px rgba(15,23,42,0.13),0 4px 18px rgba(15,23,42,0.07)";

  const eyeIcon = (visible: boolean) => visible
    ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
    : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;

  const Header = ({ title, subtitle }: { title: string; subtitle: string }) => (
    <div style={{ background:"linear-gradient(145deg,#0f172a 0%,#1e3a5f 55%,#1d4ed8 100%)", padding:"28px 28px 44px", position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", top:-60, right:-60, width:200, height:200, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.07)", pointerEvents:"none" }} />
      <div style={{ position:"absolute", top:-25, right:-25, width:110, height:110, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.05)", pointerEvents:"none" }} />
      <div style={{ position:"absolute", top:22, left:28, width:6, height:6, borderRadius:"50%", background:"rgba(251,191,36,0.7)" }} />
      <div style={{ position:"absolute", top:22, left:40, width:4, height:4, borderRadius:"50%", background:"rgba(251,191,36,0.4)" }} />
      <div style={{ position:"relative" }}>
        <div style={{ fontSize:13, color:"rgba(251,191,36,0.8)", fontFamily:"'DM Mono',monospace", letterSpacing:"0.08em", marginBottom:8 }}>OWNER PORTAL</div>
        <div style={{ fontSize:"clamp(19px,3.5vw,24px)", fontFamily:"'Syne',sans-serif", fontWeight:800, color:"#fff", letterSpacing:"-0.02em", lineHeight:1.2, marginBottom:6 }}>{title}</div>
        <div style={{ fontSize:13.5, color:"rgba(255,255,255,0.55)", lineHeight:1.5 }}>{subtitle}</div>
      </div>
      <div style={{ position:"absolute", bottom:-1, left:0, right:0, height:32, background:formBg, borderRadius:"50% 50% 0 0 / 100% 100% 0 0", transition:"background 0.35s" }} />
    </div>
  );

  return (
    <div style={{ minHeight:"100dvh", background:pageBg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"32px 16px 40px", fontFamily:"'DM Sans',sans-serif", transition:"background 0.35s" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        @keyframes riseUp { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin   { to{transform:rotate(360deg)} }
        @keyframes errIn  { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse  { 0%,100%{opacity:1} 50%{opacity:0.35} }
        .card-rp { animation: riseUp 0.5s cubic-bezier(0.22,1,0.36,1) 0.05s both; }
        .err-in  { animation: errIn 0.22s ease; }
        .tgl     { transition:all 0.2s; } .tgl:hover { opacity:0.8; }
        .own-input {
          width:100%; background:${inputBg};
          border:1.5px solid ${inputBdr}; border-radius:11px;
          padding:13px 46px 13px 44px; color:${textMain};
          font-size:15px; font-family:'DM Sans',sans-serif;
          transition:border-color 0.18s,box-shadow 0.18s;
          -webkit-appearance:none; outline:none;
        }
        .own-input:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,0.14); }
        .own-input::placeholder { color:${phClr}; }
        .own-input:disabled { opacity:0.5; cursor:not-allowed; }
        .own-btn {
          width:100%; border:none; border-radius:12px; padding:15px;
          background:linear-gradient(135deg,#1d4ed8 0%,#1e3a5f 100%);
          color:#fff; font-family:'Syne',sans-serif; font-weight:700;
          font-size:15px; letter-spacing:0.015em; cursor:pointer;
          box-shadow:0 4px 18px rgba(29,78,216,0.35);
          transition:opacity 0.18s,transform 0.12s,box-shadow 0.18s;
          display:flex; align-items:center; justify-content:center; gap:9px;
          -webkit-tap-highlight-color:transparent;
        }
        .own-btn:hover:not(:disabled) { opacity:0.88; box-shadow:0 7px 24px rgba(29,78,216,0.45); }
        .own-btn:active:not(:disabled){ transform:scale(0.98); }
        .own-btn:disabled { opacity:0.45; cursor:not-allowed; box-shadow:none; }
      `}</style>

      {/* Theme toggle */}
      <button className="tgl" onClick={() => setIsDark(d => !d)} style={{ position:"fixed", top:18, right:18, zIndex:200, background:tglBg, border:`1px solid ${tglBdr}`, borderRadius:40, padding:"7px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:6, color:tglClr, fontSize:12, fontFamily:"'DM Sans',sans-serif", fontWeight:600, backdropFilter:"blur(8px)" }}>
        {D
          ? <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/></svg>Light</>
          : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>Dark</>}
      </button>

      {/* ── Loading while session check resolves ── */}
      {validLink === null ? (
        <div style={{ color: textMuted, fontFamily:"'DM Mono',monospace", fontSize:13, letterSpacing:"0.04em" }}>Verifying link…</div>
      ) : !validLink ? (
        <div className="card-rp" style={{ width:"100%", maxWidth:420, borderRadius:24, overflow:"hidden", boxShadow:cardShadow }}>
          <Header title="⚠️ Invalid Link" subtitle="This reset link has expired or already been used." />
          <div style={{ background:formBg, padding:"24px 28px 28px", transition:"background 0.35s" }}>
            <div style={{ fontSize:13.5, color:textSub, lineHeight:1.75, marginBottom:20, textAlign:"center" }}>
              Please request a new link to reset your password.
            </div>
            <button onClick={() => navigate("/forgot-password")} className="own-btn">
              Request New Link →
            </button>
            <div style={{ paddingTop:14, marginTop:14, borderTop:`1.5px solid ${footerBdr}`, textAlign:"center" }}>
              <Link to="/login" style={{ fontSize:13, color:"#3b82f6", fontWeight:600, textDecoration:"none" }}
                onMouseEnter={e => e.currentTarget.style.textDecoration="underline"}
                onMouseLeave={e => e.currentTarget.style.textDecoration="none"}>
                ← Back to Sign In
              </Link>
            </div>
          </div>
        </div>

      ) : (
        <div className="card-rp" style={{ width:"100%", maxWidth:420, borderRadius:24, overflow:"hidden", boxShadow:cardShadow }}>
          <Header
            title={isInvite ? "👋 Welcome! Set Your Password" : "🔐 Set New Password"}
            subtitle={isInvite ? "Create a password to activate your account and get started." : "Choose a strong password for your account."}
          />

          <div style={{ background:formBg, padding:"8px 28px 28px", transition:"background 0.35s" }}>
            {done ? (
              <div style={{ textAlign:"center", padding:"16px 0" }}>
                <div style={{ fontSize:52, marginBottom:14 }}>🎉</div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:20, color:"#34d399", marginBottom:8 }}>
                  {isInvite ? "Account Activated!" : "Password Updated!"}
                </div>
                <div style={{ fontSize:13.5, color:textSub, lineHeight:1.75 }}>
                  {isInvite
                    ? <>Your password is set. Taking you to your dashboard…</>
                    : <>Your password has been changed successfully.<br/>Redirecting to sign in…</>
                  }
                </div>
                <div style={{ marginTop:22, width:24, height:24, border:"3px solid rgba(6,182,212,0.2)", borderTopColor:"#06b6d4", borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"22px auto 0" }} />
              </div>
            ) : (
              <form onSubmit={handleReset} style={{ display:"flex", flexDirection:"column", gap:14, marginTop:8 }}>

                {/* New password */}
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  <label style={{ fontSize:12.5, fontWeight:600, color:labelClr, paddingLeft:2, letterSpacing:"0.01em" }}>New password</label>
                  <div style={{ position:"relative" }}>
                    <span style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", opacity:0.55, display:"flex" }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={D ? "#94a3b8" : "#64748b"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    </span>
                    <input className="own-input" type={showPw ? "text" : "password"} value={password} onChange={e => { setPassword(e.target.value); setError(""); }} placeholder="Min. 8 characters" disabled={loading} />
                    <button type="button" onClick={() => setShowPw(p => !p)} tabIndex={-1} style={{ position:"absolute", right:13, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color: D ? "rgba(255,255,255,0.35)" : "#94a3b8", padding:4, display:"flex", alignItems:"center" }}>
                      {eyeIcon(showPw)}
                    </button>
                  </div>
                  {password && (
                    <div style={{ marginTop:2 }}>
                      <div style={{ display:"flex", gap:4, marginBottom:4 }}>
                        {Array.from({ length:5 }).map((_, i) => (
                          <div key={i} style={{ flex:1, height:3, borderRadius:2, background: i < strength.score ? strength.color : D ? "rgba(255,255,255,0.08)" : "#e2e8f0", transition:"background 0.3s" }} />
                        ))}
                      </div>
                      <div style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:strength.color }}>{strength.label}</div>
                    </div>
                  )}
                </div>

                {/* Confirm password */}
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  <label style={{ fontSize:12.5, fontWeight:600, color:labelClr, paddingLeft:2, letterSpacing:"0.01em" }}>Confirm password</label>
                  <div style={{ position:"relative" }}>
                    <span style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", opacity:0.55, display:"flex" }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={D ? "#94a3b8" : "#64748b"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    </span>
                    <input
                      className="own-input" type={showCfm ? "text" : "password"} value={confirmPw}
                      onChange={e => { setConfirmPw(e.target.value); setError(""); }}
                      placeholder="Repeat your password" disabled={loading}
                      style={{ borderColor: confirmPw ? (confirmPw === password ? "rgba(52,211,153,0.5)" : "rgba(248,113,113,0.5)") : undefined }}
                    />
                    <button type="button" onClick={() => setShowCfm(p => !p)} tabIndex={-1} style={{ position:"absolute", right:13, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color: D ? "rgba(255,255,255,0.35)" : "#94a3b8", padding:4, display:"flex", alignItems:"center" }}>
                      {eyeIcon(showCfm)}
                    </button>
                  </div>
                  {confirmPw && (
                    <div style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color: confirmPw === password ? "#34d399" : "#ef4444", marginTop:2 }}>
                      {confirmPw === password ? "✓ Passwords match" : "✗ Passwords do not match"}
                    </div>
                  )}
                </div>

                {error && (
                  <div className="err-in" style={{ background:errBg, border:`1px solid ${errBdr}`, borderRadius:10, padding:"11px 14px", display:"flex", gap:10, alignItems:"flex-start" }}>
                    <span style={{ fontSize:15, flexShrink:0, marginTop:1 }}>⚠️</span>
                    <div>
                      <div style={{ color:errTitle, fontSize:13, fontWeight:600, marginBottom:2 }}>{error}</div>
                      <div style={{ color:errHint, fontSize:11, fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>Please check and try again.</div>
                    </div>
                  </div>
                )}

                <button type="submit" disabled={loading} className="own-btn" style={{ marginTop:4 }}>
                  {loading
                    ? <><span style={{ width:16, height:16, border:"2.5px solid rgba(255,255,255,0.3)", borderTopColor:"#fff", borderRadius:"50%", display:"inline-block", animation:"spin 0.7s linear infinite" }} />Updating…</>
                    : "Update Password →"
                  }
                </button>

                <div style={{ paddingTop:14, borderTop:`1.5px solid ${footerBdr}`, display:"flex", flexDirection:"column", alignItems:"center", gap:5 }}>
                  <div style={{ fontSize:13, color:textMuted, textAlign:"center" }}>
                    Remember it?{" "}
                    <Link to="/login" style={{ color:"#3b82f6", fontWeight:600, textDecoration:"none" }}
                      onMouseEnter={e => e.currentTarget.style.textDecoration="underline"}
                      onMouseLeave={e => e.currentTarget.style.textDecoration="none"}>
                      Back to Sign In
                    </Link>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop:16, fontSize:11, color:captionClr, fontFamily:"'DM Mono',monospace", textAlign:"center" }}>
        Epic Sales Tracker · Owner Portal
      </div>
    </div>
  );
}